import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { groupBookingEndpoints } from '../api-group-bookings';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { EmptyRow, Panel, Table } from '../ui';
import { INPUT, dollars } from './event/shared';

/**
 * Group sales reports (D-376): the pipeline (by status, owner, month and
 * source, with the conversion rate), booked revenue by space and by game, and
 * how full each space was across the games in a range.
 */

const percent = (value: number | null) => (value === null ? '—' : `${Math.round(value * 1000) / 10}%`);

export default function GroupReportsPage() {
  const can = usePermissions();
  const year = new Date().getFullYear();
  const [from, setFrom] = useState(`${year}-01-01`);
  const [to, setTo] = useState(`${year + 1}-12-31`);
  const pipeline = useQuery({ queryKey: ['group-report', 'pipeline', from, to], queryFn: () => groupBookingEndpoints.pipeline(from, to) });
  const revenue = useQuery({ queryKey: ['group-report', 'revenue', from, to], queryFn: () => groupBookingEndpoints.revenue(from, to) });
  const utilization = useQuery({ queryKey: ['group-report', 'utilization', from, to], queryFn: () => groupBookingEndpoints.utilization(from, to) });
  if (!can('group_sales.view')) return <NotPermitted permission="group_sales.view" />;

  const p = pipeline.data;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <label htmlFor="gr-from">From</label>
        <input id="gr-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={INPUT} />
        <label htmlFor="gr-to">to</label>
        <input id="gr-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className={INPUT} />
        <span className="text-ink-500">Leads by the day they came in; revenue and use by the game's date.</span>
      </div>

      <Panel title="Pipeline" description={p ? `${p.totals.leads} leads: ${p.totals.won} booked, ${p.totals.lost} lost or canceled, ${p.totals.open} still open. Conversion ${percent(p.totals.conversion)}. Booked value ${dollars(p.totals.value_cents)}.` : 'Loading…'}>
        <div className="grid gap-4 p-4 lg:grid-cols-2">
          <Table headers={['Status', 'Leads', 'Booked value']}>
            {(p?.by_status ?? []).map((row) => (
              <tr key={row.status} className="border-b border-line last:border-0">
                <td className="px-5 py-2">{row.name}</td>
                <td className="tabular px-5 py-2">{row.leads}</td>
                <td className="tabular px-5 py-2">{row.value_cents ? dollars(row.value_cents) : '—'}</td>
              </tr>
            ))}
          </Table>
          <Table headers={['Owner', 'Leads', 'Open', 'Booked', 'Lost', 'Value']}>
            {(p?.by_owner ?? []).length === 0 ? <EmptyRow columns={6}>No leads in this range.</EmptyRow> : null}
            {(p?.by_owner ?? []).map((row) => (
              <tr key={row.owner_id ?? 'none'} className="border-b border-line last:border-0">
                <td className="px-5 py-2">{row.owner_name ?? 'Nobody'}</td>
                <td className="tabular px-5 py-2">{row.leads}</td>
                <td className="tabular px-5 py-2">{row.open}</td>
                <td className="tabular px-5 py-2">{row.won}</td>
                <td className="tabular px-5 py-2">{row.lost}</td>
                <td className="tabular px-5 py-2">{dollars(row.value_cents)}</td>
              </tr>
            ))}
          </Table>
          <Table headers={['Month', 'Leads', 'Booked', 'Lost', 'Value']}>
            {(p?.by_month ?? []).map((row) => (
              <tr key={row.month} className="border-b border-line last:border-0">
                <td className="px-5 py-2">{row.month}</td>
                <td className="tabular px-5 py-2">{row.leads}</td>
                <td className="tabular px-5 py-2">{row.won}</td>
                <td className="tabular px-5 py-2">{row.lost}</td>
                <td className="tabular px-5 py-2">{dollars(row.value_cents)}</td>
              </tr>
            ))}
          </Table>
          <Table headers={['Came from', 'Leads', 'Booked']}>
            {(p?.by_source ?? []).map((row) => (
              <tr key={row.source} className="border-b border-line last:border-0">
                <td className="px-5 py-2">{row.name}</td>
                <td className="tabular px-5 py-2">{row.leads}</td>
                <td className="tabular px-5 py-2">{row.won}</td>
              </tr>
            ))}
          </Table>
        </div>
      </Panel>

      <Panel title="Booked revenue" description={revenue.data ? `${revenue.data.totals.bookings} bookings, ${revenue.data.totals.tickets} tickets, ${dollars(revenue.data.totals.total_cents)} booked, ${dollars(revenue.data.totals.paid_cents)} paid.` : 'Loading…'}>
        <div className="grid gap-4 p-4 lg:grid-cols-2">
          <Table headers={['Space or package', 'Bookings', 'Tickets', 'Booked', 'Paid']}>
            {(revenue.data?.by_product ?? []).map((row) => (
              <tr key={row.product_id} className="border-b border-line last:border-0">
                <td className="px-5 py-2">{row.product_name}</td>
                <td className="tabular px-5 py-2">{row.bookings}</td>
                <td className="tabular px-5 py-2">{row.tickets}</td>
                <td className="tabular px-5 py-2">{dollars(row.total_cents)}</td>
                <td className="tabular px-5 py-2">{dollars(row.paid_cents)}</td>
              </tr>
            ))}
          </Table>
          <Table headers={['Game', 'Bookings', 'Tickets', 'Booked', 'Paid']}>
            {(revenue.data?.by_game ?? []).map((row) => (
              <tr key={row.performance_id} className="border-b border-line last:border-0">
                <td className="px-5 py-2">
                  {row.local_event_date}
                  <div className="text-xs text-ink-500">{row.event_title}</div>
                </td>
                <td className="tabular px-5 py-2">{row.bookings}</td>
                <td className="tabular px-5 py-2">{row.tickets}</td>
                <td className="tabular px-5 py-2">{dollars(row.total_cents)}</td>
                <td className="tabular px-5 py-2">{dollars(row.paid_cents)}</td>
              </tr>
            ))}
          </Table>
        </div>
      </Panel>

      <Panel title="How full the spaces were" description={utilization.data ? `${utilization.data.games} games in the range.` : 'Loading…'}>
        <Table headers={['Space', 'Games', 'Could take', 'Booked', 'Held', 'Used']}>
          {(utilization.data?.spaces ?? []).map((row) => (
            <tr key={row.product_id} className="border-b border-line last:border-0">
              <td className="px-5 py-2">{row.name}</td>
              <td className="tabular px-5 py-2">{row.games}</td>
              <td className="tabular px-5 py-2">{row.capacity}</td>
              <td className="tabular px-5 py-2">{row.booked}</td>
              <td className="tabular px-5 py-2">{row.held}</td>
              <td className="tabular px-5 py-2">{percent(row.utilization)}</td>
            </tr>
          ))}
        </Table>
      </Panel>
    </div>
  );
}
